const UNASSIGNED_VALUE = '__unassigned__';

const sortMenuItems = items =>
  items.toSorted((a, b) => {
    if (a.isSelected !== b.isSelected) {
      return Number(b.isSelected) - Number(a.isSelected);
    }

    return a.label.localeCompare(b.label);
  });

const dedupeMenuItems = items => {
  const seen = new Set();

  return items.filter(item => {
    const itemId = Number(item.value);
    if (seen.has(itemId)) {
      return false;
    }

    seen.add(itemId);
    return true;
  });
};

const buildHumanAgentItem = ({ agent, selectedUserId, action }) => ({
  label: agent.available_name || agent.name,
  value: agent.id,
  action,
  assigneeType: agent.assignee_type || 'User',
  availabilityStatus: agent.availability_status,
  thumbnail: {
    name: agent.available_name || agent.name,
    src: agent.thumbnail || agent.avatar_url || '',
  },
  isSelected:
    Number(selectedUserId) === Number(agent.id) &&
    (agent.assignee_type || 'User') !== 'AgentBot',
  raw: {
    ...agent,
    assignee_type: agent.assignee_type || 'User',
  },
});

const buildAiAgentItem = ({ agent, selectedAiAgentId, action }) => {
  const hasExplicitEmoji = Boolean(agent.emoji);

  return {
    label: hasExplicitEmoji ? agent.available_name || agent.name : agent.name,
    value: agent.id,
    action,
    assigneeType: 'AgentBot',
    emoji: hasExplicitEmoji ? agent.emoji : null,
    isSelected: Number(selectedAiAgentId) === Number(agent.id),
    raw: {
      ...agent,
      assignee_type: 'AgentBot',
    },
  };
};

export const fetchAgentTeamData = async store => {
  try {
    await store.dispatch('teams/get');
    const teams = store.getters['teams/getTeams'] || [];

    await Promise.all(
      teams.map(team =>
        store.dispatch('teamMembers/get', { teamId: team.id }).catch(() => {})
      )
    );
  } catch {
    // Ignore team-loading failures in assignment menus.
  }
};

export const buildAgentAssignmentSections = ({
  humanAgents = [],
  aiAgents = [],
  teams = [],
  getTeamMembers = () => [],
  selectedUserId = null,
  selectedAiAgentId = null,
  includeUnassigned = true,
  unassignedLabel = 'Unassigned',
  unassignedAction = 'assignAgent',
  userAction = 'assignAgent',
  aiAction = 'assignAgent',
  aiAgentsTitle = 'AI Agents',
  aiAgentsBadgeLabel = '',
  withoutTeamTitle = '',
}) => {
  const humanItems = sortMenuItems(
    (humanAgents || [])
      .filter(agent => (agent.assignee_type || 'User') !== 'AgentBot')
      .map(agent =>
        buildHumanAgentItem({
          agent,
          selectedUserId,
          action: userAction,
        })
      )
  );

  const aiItems = sortMenuItems(
    (aiAgents || []).map(agent =>
      buildAiAgentItem({
        agent,
        selectedAiAgentId,
        action: aiAction,
      })
    )
  );

  const humanItemsById = new Map(
    humanItems.map(item => [Number(item.value), item])
  );
  const assignedHumanIds = new Set();

  const teamSections = [...(teams || [])]
    .toSorted((a, b) => a.name.localeCompare(b.name))
    .map(team => {
      const members = dedupeMenuItems(
        (getTeamMembers(team.id) || [])
          .map(member => humanItemsById.get(Number(member.id)))
          .filter(Boolean)
          .filter(item => !assignedHumanIds.has(Number(item.value)))
      );

      members.forEach(item => assignedHumanIds.add(Number(item.value)));

      return {
        title: team.name,
        items: sortMenuItems(members),
      };
    })
    .filter(section => section.items.length > 0);

  const unassignedItem = {
    label: unassignedLabel,
    value: UNASSIGNED_VALUE,
    action: unassignedAction,
    assigneeType: null,
    icon: 'i-lucide-user-round',
    iconClass: 'text-n-ruby-9',
    iconContainerClass: 'bg-n-ruby-4/80',
    isSelected: !selectedUserId && !selectedAiAgentId,
    raw: {
      id: null,
      assignee_type: null,
      name: unassignedLabel,
    },
  };

  const unteamedHumanItems = humanItems.filter(
    item => !assignedHumanIds.has(Number(item.value))
  );

  return [
    ...(aiItems.length
      ? [
          {
            title: aiAgentsTitle,
            badgeLabel: aiAgentsBadgeLabel,
            items: aiItems,
          },
        ]
      : []),
    {
      title: withoutTeamTitle,
      items: [
        ...(includeUnassigned ? [unassignedItem] : []),
        ...unteamedHumanItems,
      ],
    },
    ...teamSections,
  ].filter(section => section.items.length > 0);
};

export { UNASSIGNED_VALUE };
